import React from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {useTranslation} from 'react-i18next';
import {attr, type Branch, formatUtcDateTime, getStatusColor} from '../../../../hooks/useBranches';

interface BranchTableMobileProps {
    branches: Branch[];
    loading: boolean;
    error: string | null;
    searchQuery: string;
    selected: string[];
    page?: number;        // 1-indexed, controlled by parent
    rowsPerPage?: number; // controlled by parent
    isSelected: (id: string) => boolean;
    handleRowClick: (id: string) => void;
    handleApprove: (id: string) => void;
    handleEdit: (branch: Branch) => void;
    handleReject: (id: string) => void;
}

const BranchTableMobile: React.FC<BranchTableMobileProps> = ({
                                                                 branches,
                                                                 loading,
                                                                 error,
                                                                 searchQuery,
                                                                 selected,
                                                                 page = 1,
                                                                 rowsPerPage = 10,
                                                                 isSelected,
                                                                 handleRowClick,
                                                                 handleApprove,
                                                                 handleEdit,
                                                                 handleReject,
                                                             }) => {
    const {t} = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedRowId, setSelectedRowId] = React.useState<string | null>(null);
    const open = Boolean(anchorEl);

    // Apply pagination: slice branches for current page
    const paginatedBranches = React.useMemo(() => {
        const startIndex = (page - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return branches.slice(startIndex, endIndex);
    }, [branches, page, rowsPerPage]);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedRowId(rowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRowId(null);
    };

    const handleMenuAction = (action: () => void) => {
        action();
        handleMenuClose();
    };

    if (loading) {
        return (
            <Stack spacing={2}>
                {Array.from({length: 5}).map((_, i) => (
                    <Card key={`skeleton-${i}`} variant="outlined" sx={{backgroundColor: '#fff'}}>
                        <CardContent>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                                <Skeleton variant="rectangular" height={24} width={100}/>
                                <Skeleton variant="circular" width={24} height={24}/>
                            </Box>
                            <Skeleton variant="text" width="70%" height={24} sx={{mb: 0.5}}/>
                            <Skeleton variant="text" width="90%" height={20} sx={{mb: 1}}/>
                            <Box sx={{display: 'flex', gap: 1, mt: 1.5}}>
                                <Skeleton variant="rounded" width={90} height={24}/>
                                <Skeleton variant="rounded" width={110} height={24}/>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (branches.length === 0) {
        return (
            <Box sx={{textAlign: 'center', py: 6}}>
                <AccountTreeOutlinedIcon sx={{fontSize: 48, color: 'text.disabled', mb: 1}}/>
                <Typography variant="body1" color="text.secondary">{t('hr.branches.empty.title')}</Typography>
                <Typography variant="body2" color="text.disabled">
                    {searchQuery ? t('hr.branches.empty.adjustSearch') : t('hr.branches.empty.createFirst')}
                </Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {paginatedBranches.map((row) => {
                const isItemSelected = isSelected(row.id);
                const isActive = attr(row, 'is_active', String(row.isActive)).toUpperCase() === 'TRUE';
                const status = attr(row, 'status', row.status);

                return (
                    <Card
                        key={row.id}
                        variant="outlined"
                        role="article"                        aria-label={t('hr.branches.mobile.ariaBranch', {name: row.name})}
                        sx={{
                            backgroundColor: isItemSelected ? 'action.selected' : '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:active': {
                                transform: 'scale(0.98)',
                            },
                            '&:hover': {
                                boxShadow: 2,
                            }
                        }}
                        onClick={() => handleRowClick(row.id)}
                    >
                        <CardContent sx={{pb: '12px !important'}}>
                            {/* Top row: Checkbox, Chip, Menu */}
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Checkbox
                                        color="primary"
                                        checked={isItemSelected}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => handleRowClick(row.id)}
                                        aria-label={t('hr.branches.mobile.ariaSelect', {name: row.name})}
                                        sx={{
                                            flexShrink: 0,
                                            p: 1.5, // Larger touch target
                                        }}
                                    />
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        width: 40, height: 40, borderRadius: '50%',
                                        bgcolor: (theme) => `${theme.palette.primary.main}1F`, color: 'primary.main',
                                    }}>
                                        <PlaceOutlinedIcon/>
                                    </Box>
                                    <Chip
                                        label={status}
                                        size="small"
                                        color={getStatusColor(status)}
                                        variant="outlined"
                                        sx={{fontWeight: 500}}
                                    />
                                </Box>
                                <Box onClick={(e) => e.stopPropagation()}>
                                    <Tooltip title={t('hr.branches.mobile.actionsTooltip')}>
                                        <IconButton
                                            size="small"
                                            aria-label={t('hr.branches.mobile.ariaActions', {name: row.name})}
                                            onClick={(e) => handleMenuClick(e, row.id)}
                                            sx={{
                                                bgcolor: 'action.hover',
                                                '&:hover': {bgcolor: 'action.selected'}
                                            }}
                                        >
                                            <MoreVertIcon/>
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={open && selectedRowId === row.id}
                                        onClose={handleMenuClose}
                                        slotProps={{
                                            paper: {
                                                style: {
                                                    maxHeight: 48 * 4.5,
                                                    width: '20ch',
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem onClick={() => handleMenuAction(() => handleApprove(row.id))}>
                                            <ListItemIcon><CheckCircleOutlineIcon fontSize="small"
                                                                                  color="success"/></ListItemIcon>
                                            <ListItemText>{t('common.actions.approve')}</ListItemText>
                                        </MenuItem>
                                        <MenuItem onClick={() => handleMenuAction(() => handleEdit(row))}>
                                            <ListItemIcon><EditIcon fontSize="small" color="primary"/></ListItemIcon>
                                            <ListItemText>{t('common.actions.edit')}</ListItemText>
                                        </MenuItem>
                                        <Divider/>
                                        <MenuItem onClick={() => handleMenuAction(() => handleReject(row.id))}>
                                            <ListItemIcon><CancelOutlinedIcon fontSize="small"
                                                                              color="error"/></ListItemIcon>
                                            <ListItemText>{t('common.actions.reject')}</ListItemText>
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            </Box>

                            {/* Name and code - Primary information */}
                            <Box sx={{mb: 1.5}}>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    sx={{
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        hyphens: 'auto',
                                        textAlign: 'left',
                                        mb: 0.5
                                    }}
                                >
                                    {row.name ?? '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left'}}>
                                    {t('hr.branches.table.header.code')}: {attr(row, 'code', row.code)}
                                </Typography>
                            </Box>

                            <Divider sx={{my: 1.5}}/>

                            {/* Secondary information - Compact layout */}
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant="caption" color="text.secondary">{t('hr.branches.table.header.city')}</Typography>
                                    <Typography variant="caption"
                                                fontWeight={500}>{attr(row, 'city', row.city)}</Typography>
                                </Box>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant="caption" color="text.secondary">{t('hr.branches.table.header.region')}</Typography>
                                    <Typography variant="caption"
                                                fontWeight={500}>{attr(row, 'region', row.region)}</Typography>
                                </Box>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant="caption" color="text.secondary">{t('hr.branches.table.header.isActive')}</Typography>
                                    <Chip
                                        label={isActive ? t('common.actions.yes') : t('common.actions.no')}
                                        size="small"
                                        color={isActive ? 'success' : 'default'}
                                        variant="outlined"
                                    />
                                </Box>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant="caption" color="text.secondary">{t('hr.branches.table.header.updatedAt')}</Typography>
                                    <Typography variant="caption"
                                                fontWeight={500}>{formatUtcDateTime(attr(row, 'updated_at', row.updatedAt))}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default BranchTableMobile;
